import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CellularBillingService } from '../../../../shared/services/cellular-billing.service';
import { ReconciliationDraft, MacConflict, MacResolution, ReconciliationRecord } from '../../../../shared/model/cellular-billing';

@Component({ selector: 'app-duplicate-mac-reconciliation', templateUrl: './duplicate-mac-reconciliation.component.html', styleUrls: ['./duplicate-mac-reconciliation.component.scss'] })
export class DuplicateMacReconciliationComponent implements OnChanges, OnDestroy {
  @Input() draft: ReconciliationDraft;
  @Input() readOnly = false;
  @Output() changed = new EventEmitter<ReconciliationDraft>();
  @Output() continued = new EventEmitter<void>();
  readonly sources = ['license', 'teal', 'kore'];
  sourceRecords: { [source: string]: ReconciliationRecord[] } = {};
  search = ''; statusFilter = 'OPEN'; sourceFilter = ''; selectedId = '';
  form: MacResolution;
  dirty = false;
  busy = false;
  error = '';
  notice = '';
  private advanceAfterSave = false;
  private request: Subscription;
  constructor(private billing: CellularBillingService) {}
  get active(): MacConflict { return this.draft && this.draft.conflicts.find(conflict => conflict.id === this.selectedId); }
  get locked(): boolean { return this.readOnly || this.busy || !this.draft || this.draft.status === 'COMPLETE'; }
  ngOnChanges(): void {
    if (!this.draft) return;
    if (this.advanceAfterSave) {
      const next = this.queue.find(conflict => this.status(conflict) !== 'RESOLVED') || this.draft.conflicts.find(conflict => this.status(conflict) !== 'RESOLVED');
      this.selectedId = next ? next.id : this.selectedId;
      this.advanceAfterSave = false;
    }
    if (!this.active) this.selectedId = this.draft.conflicts.length ? this.draft.conflicts[0].id : '';
    this.initializeForm();
  }
  ngOnDestroy(): void { if (this.request) this.request.unsubscribe(); }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty) { event.preventDefault(); event.returnValue = ''; }
  }
  get queue(): MacConflict[] {
    if (!this.draft) return [];
    const search = this.search.toLowerCase().trim();
    return this.draft.conflicts.filter(conflict => (!this.sourceFilter || conflict.duplicateSources.includes(this.sourceFilter))
      && (this.statusFilter === 'ALL' || (this.statusFilter === 'OPEN' ? this.status(conflict) !== 'RESOLVED' : this.status(conflict) === this.statusFilter))
      && (!search || [conflict.mac, ...conflict.records.map(record => [record.msp, record.organization, record.deviceName, record.iccid].join(' '))].join(' ').toLowerCase().includes(search)))
      .sort((a, b) => Number(b.severity === 'CRITICAL') - Number(a.severity === 'CRITICAL') || a.mac.localeCompare(b.mac));
  }
  status(conflict: MacConflict): string { return this.draft.decisions[conflict.id] ? this.draft.decisions[conflict.id].status : 'OPEN'; }
  statusLabel(conflict: MacConflict): string {
    return { OPEN: 'Unresolved', DRAFT: 'Draft saved', INVESTIGATION: 'Needs investigation', RESOLVED: 'Resolved' }[this.status(conflict)] || 'Unresolved';
  }
  select(conflict: MacConflict): void {
    if (this.busy || (this.dirty && !window.confirm('Discard unsaved conflict edits? Use Save draft to preserve them.'))) return;
    this.selectedId = conflict.id; this.error = ''; this.initializeForm();
  }
  private initializeForm(): void {
    const conflict = this.active;
    if (!conflict) { this.form = null; return; }
    const decision = this.draft.decisions[conflict.id];
    const saved = this.draft.work && this.draft.work[conflict.id] || decision && decision.resolution;
    this.sourceRecords = {};
    this.form = saved ? JSON.parse(JSON.stringify(saved)) : { identityRecordId: '', verificationSource: '', note: decision && decision.note || '', distinctUsageConfirmed: false, sources: {} };
    this.sources.forEach(source => {
      const records = conflict.records.filter(record => record.source === source);
      this.sourceRecords[source] = records;
      if (!this.form.sources[source] && records.length) this.form.sources[source] = { action: 'SELECT', recordIds: records.length === 1 ? [records[0].id] : [] };
    });
    if (!this.form.identityRecordId && this.sourceRecords.license.length === 1) this.form.identityRecordId = this.sourceRecords.license[0].id;
    this.dirty = false;
  }
  choose(record: ReconciliationRecord, checked: boolean): void {
    const choice = this.form.sources[record.source];
    choice.recordIds = checked ? [...choice.recordIds.filter(id => id !== record.id), record.id] : choice.recordIds.filter(id => id !== record.id);
    this.dirty = true;
  }
  setIdentity(record: ReconciliationRecord): void {
    this.form.identityRecordId = record.id;
    if (this.form.sources.license.action !== 'COMBINE') this.form.sources.license.recordIds = [record.id];
    this.dirty = true;
  }
  usage(source: string): number | null {
    const choice = this.form && this.form.sources[source];
    if (!choice || !choice.recordIds.length || (choice.action !== 'COMBINE' && choice.recordIds.length !== 1) || (choice.action === 'COMBINE' && choice.recordIds.length < 2)) return null;
    const records = this.sourceRecords[source].filter(record => choice.recordIds.includes(record.id));
    if (records.some(record => !record.usageValid)) return null;
    if (source === 'license') {
      const identity = this.sourceRecords.license.find(record => record.id === this.form.identityRecordId);
      if (!identity || records.some(record => record.msp.toLowerCase() !== identity.msp.toLowerCase() || record.organization.toLowerCase() !== identity.organization.toLowerCase())) return null;
    }
    return Math.round(records.reduce((sum, record) => sum + record.usageGb, 0) * 1e6) / 1e6;
  }
  get afterUsage(): number | null { return this.form ? this.usage(this.form.verificationSource === 'cp' ? 'license' : this.form.verificationSource) : null; }
  get afterCharge(): number | null { return this.afterUsage === null ? null : Math.max(0, Math.ceil(Math.round(((this.afterUsage - 1) * .95 + Number.EPSILON) * 1e6) / 1e6)); }
  get combines(): boolean { return !!this.form && Object.keys(this.form.sources).some(source => this.form.sources[source].action === 'COMBINE'); }
  canCombineSource(source: string): boolean {
    const records = this.sourceRecords[source].filter(record => record.usageValid);
    if (source !== 'license') return records.length > 1;
    const identity = records.find(record => record.id === this.form.identityRecordId);
    return !!identity && records.filter(record => record.msp.toLowerCase() === identity.msp.toLowerCase() && record.organization.toLowerCase() === identity.organization.toLowerCase()).length > 1;
  }
  get canResolve(): boolean {
    return !this.locked && !!this.form && !!this.form.identityRecordId && this.form.note.trim().length >= 5 && this.form.note.length <= 500
      && (this.dirty || this.status(this.active) !== 'RESOLVED')
      && this.afterUsage !== null && (!this.combines || this.form.distinctUsageConfirmed)
      && this.sources.every(source => !this.sourceRecords[source].length || this.usage(source) !== null)
      && Object.keys(this.form.sources).every(source => this.form.sources[source].action !== 'COMBINE' || this.canCombineSource(source));
  }
  get poolImpact() {
    return this.draft && this.draft.report && this.draft.report.pooling ? this.draft.report.pooling.pools.filter(pool => pool.members.some(member => member.mac === this.selectedId)) : [];
  }
  get history() { return this.draft ? this.draft.events.filter(event => event.conflictId === this.selectedId).slice().reverse().slice(0, 10) : []; }
  save(action: 'RESOLVE' | 'INVESTIGATE' | 'SAVE_WORK' | 'RESET', next = false): void {
    if (this.locked || !this.active || (action === 'RESOLVE' && !this.canResolve)) return;
    if (action === 'RESOLVE' && (this.combines || Object.keys(this.form.sources).some(source => this.form.sources[source].action === 'EXCLUDE'))
      && !window.confirm(`Apply this decision? Verified usage will be ${this.afterUsage} GB. Only explicitly selected records are retained. The originals remain in the audit draft.`)) return;
    this.advanceAfterSave = next;
    this.submit({ revision: this.draft.revision, action, conflictId: this.selectedId, resolution: this.form, work: this.form, note: this.form.note });
  }
  complete(): void {
    if (this.busy || this.dirty || !this.draft || this.draft.summary.unresolved) return;
    if (this.draft.status === 'COMPLETE') { this.continued.emit(); return; }
    if (this.readOnly) return;
    this.submit({ revision: this.draft.revision, action: 'COMPLETE' }, true);
  }
  reload(): void {
    if (this.busy || (this.dirty && !window.confirm('Discard unsaved edits and reload the saved draft?'))) return;
    this.busy = true;
    this.request = this.billing.getReconciliation(this.draft._id).pipe(finalize(() => this.busy = false)).subscribe(draft => { this.dirty = false; this.changed.emit(draft); }, () => this.error = 'Unable to reload the draft. Your edits are still on screen.');
  }
  private submit(body: object, complete = false): void {
    this.busy = true; this.error = '';
    this.request = this.billing.changeReconciliation(this.draft._id, body).pipe(finalize(() => this.busy = false)).subscribe(draft => {
      this.dirty = false; this.notice = complete ? 'Reconciliation completed.' : 'Decision saved to the draft.';
      this.changed.emit(draft);
      if (complete) this.continued.emit();
    }, error => {
      this.advanceAfterSave = false;
      const message = error && error.error && error.error.error && error.error.error.message;
      this.error = typeof message === 'string' ? message : error && error.status === 404
        ? 'The reconciliation save route is unavailable. Check the gateway deployment. Your edits are preserved.'
        : 'Unable to save. Your edits are preserved; retry or reload the draft.';
    });
  }
}
